import { useState } from "react";
import { BookOpen, Star, Languages, Sparkles, Layers } from "lucide-react";

// Fee schedule sourced from the user's reference price list. Currency rows in
// fixed display order; the "highlight" prop lets us bold a viewer's local
// currency on country-specific pages.
const COURSES = [
  {
    id: "recitation",
    icon: BookOpen,
    title: "Quran Recitation with Tajweed",
    duration: "30 minutes / class",
    rows: [
      { ccy: "USD ($)",   p5: 47,  p3: 37,  p2: 30 },
      { ccy: "GBP (£)",   p5: 36,  p3: 30,  p2: 24 },
      { ccy: "CAD ($)",   p5: 57,  p3: 52,  p2: 36 },
      { ccy: "EUR (€)",   p5: 42,  p3: 36,  p2: 30 },
      { ccy: "AUD ($)",   p5: 62,  p3: 52,  p2: 41 },
      { ccy: "SAR (﷼)",   p5: 220, p3: 190, p2: 167 },
      { ccy: "AED",       p5: 170, p3: 135, p2: 110 },
    ],
  },
  {
    id: "memorization",
    icon: Star,
    title: "Quran Memorization (Hifz)",
    duration: "45 minutes / class",
    rows: [
      { ccy: "USD ($)",   p5: 80,  p3: 60,  p2: 40 },
      { ccy: "GBP (£)",   p5: 55,  p3: 40,  p2: 30 },
      { ccy: "CAD ($)",   p5: 100, p3: 80,  p2: 55 },
      { ccy: "EUR (€)",   p5: 70,  p3: 55,  p2: 35 },
      { ccy: "AUD ($)",   p5: 100, p3: 80,  p2: 55 },
      { ccy: "SAR (﷼)",   p5: 330, p3: 260, p2: 155 },
      { ccy: "AED",       p5: 290, p3: 220, p2: 146 },
    ],
  },
  {
    id: "translation",
    icon: Languages,
    title: "Translation & Tafseer",
    duration: "30 minutes / class",
    rows: [
      { ccy: "USD ($)",   p5: 80,  p3: 60,  p2: 40 },
      { ccy: "GBP (£)",   p5: 55,  p3: 40,  p2: 30 },
      { ccy: "CAD ($)",   p5: 100, p3: 80,  p2: 55 },
      { ccy: "EUR (€)",   p5: 70,  p3: 55,  p2: 35 },
      { ccy: "AUD ($)",   p5: 100, p3: 80,  p2: 55 },
      { ccy: "SAR (﷼)",   p5: 330, p3: 260, p2: 155 },
      { ccy: "AED",       p5: 290, p3: 220, p2: 146 },
    ],
  },
  {
    id: "tajweed",
    icon: Sparkles,
    title: "Tajweed Course",
    duration: "30 minutes / class",
    rows: [
      { ccy: "USD ($)",   p5: 45,  p3: 35,  p2: 28 },
      { ccy: "GBP (£)",   p5: 34,  p3: 28,  p2: 22 },
      { ccy: "CAD ($)",   p5: 55,  p3: 50,  p2: 34 },
      { ccy: "EUR (€)",   p5: 40,  p3: 34,  p2: 28 },
      { ccy: "AUD ($)",   p5: 60,  p3: 50,  p2: 39 },
      { ccy: "SAR (﷼)",   p5: 200, p3: 170, p2: 147 },
      { ccy: "AED",       p5: 165, p3: 130, p2: 102 },
    ],
  },
  {
    id: "juz30",
    icon: Layers,
    title: "30th Juz / Chapter Memorization",
    duration: "45 minutes / class",
    rows: [
      { ccy: "USD ($)",   p5: 60,  p3: 50,  p2: 40 },
      { ccy: "GBP (£)",   p5: 45,  p3: 30,  p2: 25 },
      { ccy: "CAD ($)",   p5: 80,  p3: 70,  p2: 55 },
      { ccy: "EUR (€)",   p5: 50,  p3: 45,  p2: 35 },
      { ccy: "AUD ($)",   p5: 80,  p3: 70,  p2: 55 },
      { ccy: "SAR (﷼)",   p5: 300, p3: 250, p2: 200 },
      { ccy: "AED",       p5: 220, p3: 183, p2: 146 },
    ],
  },
];

// Map country slug → currency-row prefix so we can highlight the viewer's
// likely currency. Falls back gracefully.
const CCY_FOR_COUNTRY = {
  uk: "GBP",
  usa: "USD",
  canada: "CAD",
  australia: "AUD",
};

// Per-month fees. Each plan column header includes "/ month".
const PLAN_HEADERS = [
  { key: "p5", label: "5 days / week", sub: "Mon – Fri" },
  { key: "p3", label: "3 days / week", sub: "Mon · Wed · Fri" },
  { key: "p2", label: "2 days / week", sub: "Sat · Sun" },
];

export const FeesChart = ({ highlightCurrency, onBook, defaultCourse = "recitation" }) => {
  const [active, setActive] = useState(defaultCourse);
  const course = COURSES.find(c => c.id === active) || COURSES[0];
  const highlightKey = highlightCurrency || CCY_FOR_COUNTRY[(window.location.pathname.match(/\/online-quran-classes\/([a-z]+)/) || [])[1]] || null;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" aria-labelledby="fees-heading" data-testid="fees-chart">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-accent">Monthly Hadya · transparent · no hidden cost</p>
          <h2 id="fees-heading" className="font-display text-3xl sm:text-4xl mt-1">Hadya for Quran Teachers</h2>
          <p className="mt-2 max-w-2xl text-foreground/70">
            A modest monthly Hadya supports the teacher who guides your Quran journey — chosen with
            barakah in mind. Pick your course, then a schedule that fits your week. The first trial class is free.
          </p>
        </div>
        {onBook && (
          <button
            type="button"
            onClick={onBook}
            data-testid="fees-book-trial-btn"
            className="pill-btn h-11 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
          >
            Book Free Trial →
          </button>
        )}
      </div>

      {/* Course tabs */}
      <div
        role="tablist"
        aria-label="Course type"
        className="mt-6 flex flex-wrap gap-2"
        data-testid="fees-course-tabs"
      >
        {COURSES.map(c => {
          const isActive = c.id === active;
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(c.id)}
              data-testid={`fees-tab-${c.id}`}
              className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm border transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-card/60 border-border text-foreground/75 hover:text-foreground hover:bg-muted"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{c.title}</span>
            </button>
          );
        })}
      </div>

      {/* Active table */}
      <div className="mt-6 rounded-2xl border border-accent/35 bg-card overflow-hidden shadow-sm">
        <div className="px-5 sm:px-6 py-4 border-b border-border bg-primary/[0.04] flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <course.icon className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg">{course.title}</h3>
          </div>
          <span className="text-xs text-foreground/65">{course.duration}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`fees-table-${course.id}`}>
            <thead>
              <tr className="text-left bg-muted/40">
                <th className="px-4 sm:px-5 py-3 font-medium text-foreground/70 w-[28%]">Currency</th>
                {PLAN_HEADERS.map(h => (
                  <th key={h.key} className="px-4 sm:px-5 py-3 font-medium text-foreground/70">
                    <div>{h.label}</div>
                    <div className="text-[11px] text-foreground/50 font-normal">{h.sub}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {course.rows.map((r, i) => {
                const isHi = highlightKey && r.ccy.startsWith(highlightKey);
                return (
                  <tr
                    key={r.ccy}
                    className={`border-t border-border/60 ${
                      isHi ? "bg-accent/10" : i % 2 === 1 ? "bg-muted/20" : ""
                    }`}
                    data-testid={`fees-row-${course.id}-${r.ccy.split(" ")[0].toLowerCase()}`}
                  >
                    <td className={`px-4 sm:px-5 py-3 ${isHi ? "font-semibold text-primary" : "text-foreground/85"}`}>
                      {r.ccy} {isHi && <span className="ml-1 text-[10px] uppercase tracking-wider text-accent">your region</span>}
                    </td>
                    <td className={`px-4 sm:px-5 py-3 ${isHi ? "font-semibold" : ""}`}>{r.p5}<span className="text-foreground/45 text-xs"> /mo</span></td>
                    <td className={`px-4 sm:px-5 py-3 ${isHi ? "font-semibold" : ""}`}>{r.p3}<span className="text-foreground/45 text-xs"> /mo</span></td>
                    <td className={`px-4 sm:px-5 py-3 ${isHi ? "font-semibold" : ""}`}>{r.p2}<span className="text-foreground/45 text-xs"> /mo</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3.5 border-t border-border bg-muted/20 text-xs text-foreground/65 flex flex-wrap gap-x-5 gap-y-1">
          <span>✓ 1-to-1 live with a verified teacher</span>
          <span>✓ Free trial class</span>
          <span>✓ Family / sibling discounts on request</span>
          <span>✓ Pause or cancel anytime</span>
        </div>
      </div>
    </section>
  );
};
